import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/cn';
import { timeAgo } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Pagination } from '@/components/ui/pagination';
import { apiErrorMessage } from '@shared/api/client';
import type {
  TransactionStatus,
  Uuid,
} from '@shared/types/api';
import {
  useAdminTransaction,
  useAdminTransactions,
  useAdminTxHistory,
  useAdminUsers,
  useAssignAssociate,
  useForceCancel,
  useForceComplete,
  useRevokeAssignment,
} from '@/hooks/use-admin';
import { useSectionPermission } from '@/hooks/use-admin-permissions';

const STATUS_TABS: ({ value: TransactionStatus | 'ALL'; label: string })[] = [
  { value: 'ALL', label: 'All' },
  { value: 'INITIATED', label: 'Initiated' },
  { value: 'INVOICE_CREATED', label: 'Invoice Sent' },
  { value: 'PAID', label: 'Paid' },
  { value: 'ASSIGNED', label: 'Assigned' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'FINAL_DOC_READY', label: 'Final Ready' },
  { value: 'COMPLETE', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const STATUS_TONE: Record<TransactionStatus, string> = {
  SERVICE_REQUESTED: 'bg-indigo-50 text-indigo-700',
  CHAT_CREATED: 'bg-sky-50 text-sky-700',
  ASSOCIATE_ASSIGNMENT_PENDING: 'bg-amber-50 text-amber-800',
  ASSOCIATE_ASSIGNED: 'bg-blue-50 text-blue-800',
  ASSOCIATE_ACCEPTANCE_PENDING: 'bg-amber-50 text-amber-800',
  ASSOCIATE_ACCEPTED: 'bg-blue-50 text-blue-800',
  QUOTATION_REVIEW_PENDING: 'bg-amber-50 text-amber-800',
  CLIENT_CONFIRMATION_PENDING: 'bg-teal-50 text-teal-800',
  CLIENT_CONFIRMED: 'bg-teal-50 text-teal-800',
  PAYMENT_PENDING: 'bg-amber-50 text-amber-800',
  INITIATED: 'bg-gray-100 text-gray-700',
  INVOICE_CREATED: 'bg-amber-50 text-amber-800',
  PAID: 'bg-blue-50 text-blue-800',
  PAYMENT_RECEIVED: 'bg-blue-50 text-blue-800',
  ASSIGNED: 'bg-blue-50 text-blue-800',
  ASSIGNED_REVOKED: 'bg-gray-100 text-gray-700',
  IN_PROGRESS: 'bg-brand-50 text-brand-800',
  DRAFT_PREPARATION: 'bg-brand-50 text-brand-800',
  CLIENT_DRAFT_CONFIRMATION: 'bg-purple-50 text-purple-800',
  REVIEW: 'bg-purple-50 text-purple-800',
  REVISION_REQUESTED: 'bg-orange-50 text-orange-800',
  SENT_FOR_APPROVAL: 'bg-indigo-50 text-indigo-800',
  APPROVED: 'bg-brand-50 text-brand-800',
  FINAL_RESULT_RECEIVED: 'bg-teal-50 text-teal-800',
  DELIVERED: 'bg-emerald-50 text-emerald-800',
  FINAL_DOC_READY: 'bg-emerald-50 text-emerald-800',
  GRIEVANCE_RAISED: 'bg-rose-50 text-rose-800',
  CLIENT_CONFIRMED_FINAL: 'bg-emerald-50 text-emerald-800',
  RATING_PENDING: 'bg-amber-50 text-amber-800',
  SETTLEMENT_PENDING: 'bg-amber-50 text-amber-800',
  SETTLED: 'bg-emerald-50 text-emerald-800',
  COMPLETE: 'bg-gray-100 text-gray-700',
  CLOSED: 'bg-gray-100 text-gray-700',
  CANCELLED: 'bg-red-50 text-red-700',
};

const STATUS_LABEL: Record<TransactionStatus, string> = {
  SERVICE_REQUESTED: 'Service Requested',
  CHAT_CREATED: 'Chat Created',
  ASSOCIATE_ASSIGNMENT_PENDING: 'Assignment Pending',
  ASSOCIATE_ASSIGNED: 'Associate Assigned',
  ASSOCIATE_ACCEPTANCE_PENDING: 'Acceptance Pending',
  ASSOCIATE_ACCEPTED: 'Associate Accepted',
  QUOTATION_REVIEW_PENDING: 'Quotation Review Pending',
  CLIENT_CONFIRMATION_PENDING: 'Client Confirmation Pending',
  CLIENT_CONFIRMED: 'Associate Verified',
  PAYMENT_PENDING: 'Payment Pending',
  INITIATED: 'Chat Created',
  INVOICE_CREATED: 'Invoice Sent',
  PAID: 'Paid',
  PAYMENT_RECEIVED: 'Payment Received',
  ASSIGNED: 'Associate Assigned',
  ASSIGNED_REVOKED: 'Reassigning',
  IN_PROGRESS: 'In Progress',
  DRAFT_PREPARATION: 'Draft Preparation',
  CLIENT_DRAFT_CONFIRMATION: 'Draft Under Review',
  REVIEW: 'In Review',
  REVISION_REQUESTED: 'Revision Requested',
  SENT_FOR_APPROVAL: 'Sent For Approval',
  APPROVED: 'Approved',
  FINAL_RESULT_RECEIVED: 'Final Result Ready',
  DELIVERED: 'Delivered',
  FINAL_DOC_READY: 'Final Doc Ready',
  GRIEVANCE_RAISED: 'Grievance / Cancellation Pending',
  CLIENT_CONFIRMED_FINAL: 'Client Confirmed',
  RATING_PENDING: 'Rating Pending',
  SETTLEMENT_PENDING: 'Settlement Pending',
  SETTLED: 'Payout Settled',
  COMPLETE: 'Completed',
  CLOSED: 'Closed',
  CANCELLED: 'Cancelled',
};

export function AdminTransactionsPage() {
  const { canEdit } = useSectionPermission('TRANSACTIONS');
  const [tab, setTab] = useState<TransactionStatus | 'ALL'>('ALL');
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [selectedId, setSelectedId] = useState<Uuid | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQ(q);
    }, 300);
    return () => clearTimeout(timer);
  }, [q]);

  const { data, isLoading } = useAdminTransactions({
    status: tab === 'ALL' ? undefined : tab,
    q: debouncedQ.trim() || undefined,
    page,
    pageSize,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;

  return (
    <div className="w-full px-4 py-6 md:px-6 md:py-8">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Transactions</h1>
        <p className="text-sm text-gray-600">
          Every client transaction. Assign associates, revoke assignments, or force-close stuck rows.
        </p>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3 w-full">
        <Input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          placeholder="Search code / client email / associate email"
          className="w-full md:max-w-md"
        />
      </div>

      <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-gray-200 pb-2">
        <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar scroll-smooth py-1 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap">
          {STATUS_TABS.map((t) => (
            <button
              key={t.value}
              onClick={() => {
                setTab(t.value);
                setPage(1);
              }}
              className={cn(
                'shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors whitespace-nowrap',
                tab === t.value
                  ? 'bg-brand-700 text-white shadow-xs'
                  : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200/60 sm:border-0',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="hidden sm:block shrink-0">
          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            totalPages={totalPages}
            onPageChange={setPage}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setPage(1);
            }}
            isLoading={isLoading}
            showBorder={false}
            showRange={false}
            showPageSize={false}
            compact
          />
        </div>
      </div>

      <section className="mt-4 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xs">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm text-left">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 font-medium whitespace-nowrap">Code</th>
              <th className="px-4 py-3 font-medium whitespace-nowrap">Service</th>
              <th className="px-4 py-3 font-medium whitespace-nowrap">Client</th>
              <th className="px-4 py-3 font-medium whitespace-nowrap">Associate</th>
              <th className="px-4 py-3 font-medium whitespace-nowrap">Status</th>
              <th className="px-4 py-3 font-medium whitespace-nowrap">Updated</th>
              <th className="px-4 py-3 text-right font-medium pr-6 whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading ? (
              <tr><td colSpan={7} className="p-6 text-center text-gray-500">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="p-6 text-center text-gray-500">No transactions match.</td></tr>
            ) : items.map((t) => (
              <tr key={t.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-mono text-xs whitespace-nowrap font-semibold text-gray-900">{t.code}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="font-medium text-gray-900">{t.service.name}</div>
                  {t.subService?.name ? (
                    <div className="text-xs text-gray-500">{t.subService.name}</div>
                  ) : null}
                </td>
                <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                  {t.client
                    ? `${t.client.firstName ?? ''} ${t.client.lastName ?? ''}`.trim() || t.client.email
                    : '—'}
                </td>
                <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                  {t.assignedAssociate
                    ? `${t.assignedAssociate.firstName ?? ''} ${t.assignedAssociate.lastName ?? ''}`.trim() ||
                      t.assignedAssociate.email
                    : <span className="text-amber-700 font-medium">unassigned</span>}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className={cn('inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium uppercase', STATUS_TONE[t.status])}>
                    {STATUS_LABEL[t.status] ?? t.status.replace(/_/g, ' ')}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{timeAgo(t.updatedAt)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap pr-6">
                  <div className="flex justify-end gap-3 text-sm">
                    {/* Full-page view — chat + state transitions + history.
                        This is the primary entry point now; the compact
                        Manage dialog below is retained for quick edits. */}
                    <Link
                      to={`/transactions/${t.id}`}
                      className="inline-flex items-center rounded-md border border-brand-200 bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand-700 hover:bg-brand-100"
                    >
                      {canEdit ? 'View / Chat' : 'View'}
                    </Link>
                    {canEdit ? (
                      <button
                        onClick={() => setSelectedId(t.id)}
                        className="text-xs text-gray-600 hover:underline"
                      >
                        Manage
                      </button>
                    ) : (
                      <button
                        onClick={() => setSelectedId(t.id)}
                        className="text-xs text-gray-500 hover:underline"
                      >
                        Details
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          totalPages={totalPages}
          onPageChange={setPage}
          onPageSizeChange={(newSize) => {
            setPageSize(newSize);
            setPage(1);
          }}
          isLoading={isLoading}
        />
      </section>

      <TxDetailDialog
        id={selectedId}
        open={selectedId !== null}
        onClose={() => setSelectedId(null)}
      />
    </div>
  );
}

// ---------- detail dialog ----------

function TxDetailDialog({ id, open, onClose }: { id: Uuid | null; open: boolean; onClose: () => void }) {
  const { canEdit, canDelete } = useSectionPermission('TRANSACTIONS');
  const { data: tx, isLoading } = useAdminTransaction(id ?? undefined);
  const { data: history } = useAdminTxHistory(id ?? undefined);

  const [panel, setPanel] = useState<'assign' | 'revoke' | 'cancel' | null>(null);
  const qc = useQueryClient();

  const canAssign = canEdit && tx && (tx.status === 'PAID' || tx.status === 'ASSIGNED_REVOKED');
  const canRevoke =
    canEdit &&
    tx &&
    [
      'ASSIGNED',
      'IN_PROGRESS',
      'REVIEW',
      'REVISION_REQUESTED',
      'SENT_FOR_APPROVAL',
      'APPROVED',
    ].includes(tx.status);
  const canForceComplete = canEdit && tx && tx.status === 'FINAL_DOC_READY';
  const canForceCancel =
    canDelete && tx && !['COMPLETE', 'CANCELLED'].includes(tx.status);

  return (
    <Dialog
      open={open}
      onClose={() => {
        setPanel(null);
        onClose();
      }}
      title={tx ? `Transaction ${tx.code}` : 'Transaction'}
      className="max-w-3xl"
      footer={<Button variant="ghost" onClick={() => { setPanel(null); onClose(); }}>Close</Button>}
    >
      {isLoading || !tx ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : panel === 'assign' ? (
        <AssignPanel
          txId={tx.id}
          initialCandidateIds={
            tx.candidateAssociates
              ? tx.candidateAssociates.filter((c) => c.status !== 'DECLINED' && c.status !== 'EXPIRED').map((c) => c.id)
              : tx.associateAssignmentStatus === 'DECLINED'
              ? []
              : tx.candidateAssociateIds ?? []
          }
          onDone={() => {
            setPanel(null);
            qc.invalidateQueries({ queryKey: ['admin', 'tx'] });
          }}
        />
      ) : panel === 'revoke' ? (
        <ReasonPanel
          title="Revoke assignment"
          description="The transaction returns to ASSIGNED_REVOKED and can be reassigned."
          onSubmit={(r) => r}
          kind="revoke"
          txId={tx.id}
          onDone={() => setPanel(null)}
        />
      ) : panel === 'cancel' ? (
        <ReasonPanel
          title="Force-cancel"
          description="Admin override cancel. Shown to the client/associate as the reason."
          kind="cancel"
          txId={tx.id}
          onDone={() => setPanel(null)}
        />
      ) : (
        <>
          <section className="grid gap-4 sm:grid-cols-2 text-sm">
            <Field label="Status" value={tx.status.replace(/_/g, ' ')} />
            <Field label="Service" value={`${tx.service.name}${tx.subService?.name ? ' / ' + tx.subService.name : ''}`} />
            <Field
              label="Client"
              value={
                tx.client
                  ? `${tx.client.firstName ?? ''} ${tx.client.lastName ?? ''}`.trim() || tx.client.email
                  : '—'
              }
              sub={tx.client?.email}
            />
            <Field
              label="Associate"
              value={
                tx.assignedAssociate
                  ? `${tx.assignedAssociate.firstName ?? ''} ${tx.assignedAssociate.lastName ?? ''}`.trim() ||
                    tx.assignedAssociate.email
                  : '—'
              }
              sub={tx.assignedAssociate?.email}
            />
            <Field label="Initiated" value={new Date(tx.initiatedAt).toLocaleString()} />
            <Field label="Updated" value={new Date(tx.updatedAt).toLocaleString()} />
            {tx.cancellationReason ? (
              <Field label="Cancellation reason" value={tx.cancellationReason} />
            ) : null}
          </section>

          <div className="mt-5 flex flex-wrap gap-2 border-t border-gray-100 pt-4">
            {canAssign ? (
              <Button onClick={() => setPanel('assign')}>Assign associate</Button>
            ) : null}
            {canRevoke ? (
              <Button variant="outline" onClick={() => setPanel('revoke')}>Revoke assignment</Button>
            ) : null}
            {canForceComplete ? (
              <ForceCompleteButton txId={tx.id} />
            ) : null}
            {canForceCancel ? (
              <Button variant="outline" onClick={() => setPanel('cancel')}>Force-cancel</Button>
            ) : null}
          </div>

          <section className="mt-6">
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              State history
            </h3>
            <ol className="mt-2 space-y-1.5 text-xs text-gray-700">
              {(history ?? []).map((h) => (
                <li key={h.id} className="flex gap-3">
                  <span className="text-gray-400">{new Date(h.createdAt).toLocaleString()}</span>
                  <span>
                    {h.fromStatus ? `${h.fromStatus} → ` : ''}
                    <span className="font-medium">{h.toStatus}</span>
                    {h.actor ? (
                      <span className="text-gray-500"> by {h.actor.email}</span>
                    ) : null}
                    {h.reason ? <span className="italic text-gray-600"> — {h.reason}</span> : null}
                  </span>
                </li>
              ))}
            </ol>
          </section>
        </>
      )}
    </Dialog>
  );
}

function Field({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="font-medium text-gray-900">{value}</div>
      {sub ? <div className="text-xs text-gray-500">{sub}</div> : null}
    </div>
  );
}

function ForceCompleteButton({ txId }: { txId: Uuid }) {
  const complete = useForceComplete();
  return (
    <Button onClick={() => complete.mutate(txId)} disabled={complete.isPending}>
      {complete.isPending ? 'Completing…' : 'Force complete'}
    </Button>
  );
}

// Assign an associate sub-panel — searches admin users by role + text
function AssignPanel({
  txId,
  initialCandidateIds = [],
  onDone,
}: {
  txId: Uuid;
  initialCandidateIds?: string[];
  onDone: () => void;
}) {
  const [q, setQ] = useState('');
  const [reason, setReason] = useState('');
  const [chosenIds, setChosenIds] = useState<string[]>(initialCandidateIds);
  const { data } = useAdminUsers({ role: 'ASSOCIATE', q: q.trim() || undefined, pageSize: 20 });
  const { data: franchise } = useAdminUsers({ role: 'FRANCHISE', q: q.trim() || undefined, pageSize: 20 });
  const assign = useAssignAssociate();

  const candidates = [...(data?.items ?? []), ...(franchise?.items ?? [])].slice(0, 30);

  const toggleSelect = (id: string) => {
    setChosenIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((item) => item !== id);
      }
      if (prev.length >= 5) {
        return prev;
      }
      return [...prev, id];
    });
  };

  const isLimitReached = chosenIds.length >= 5;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-gray-700">
          Select 1 to 5 KYC-VERIFIED Associates to receive assignment request.
        </p>
        <span className="shrink-0 text-xs font-semibold px-2 py-0.5 rounded bg-brand-50 text-brand-700 border border-brand-200">
          Selected ({chosenIds.length}/5)
        </span>
      </div>

      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search by name / email / mobile"
      />

      {isLimitReached && (
        <p className="text-xs text-amber-700 font-medium">
          Maximum 5 associates selected. Uncheck a candidate to select a different associate.
        </p>
      )}

      <ul className="max-h-52 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200">
        {candidates.length === 0 ? (
          <li className="p-3 text-sm text-gray-500">No candidates found.</li>
        ) : (
          candidates.map((u) => {
            const isSelected = chosenIds.includes(u.id);
            const isDisabled = !isSelected && isLimitReached;

            return (
              <li key={u.id}>
                <label
                  className={cn(
                    'flex w-full items-center justify-between px-3 py-2 text-left text-sm cursor-pointer select-none transition-colors',
                    isSelected && 'bg-brand-50/70',
                    isDisabled && 'opacity-50 cursor-not-allowed bg-gray-50',
                    !isSelected && !isDisabled && 'hover:bg-gray-50',
                  )}
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={isDisabled}
                      onChange={() => toggleSelect(u.id)}
                      className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
                    />
                    <div>
                      <span className="block font-medium text-gray-900">
                        {[u.firstName, u.lastName].filter(Boolean).join(' ') || u.email}
                      </span>
                      <span className="block text-xs text-gray-500">
                        {u.email}
                        {u.status !== 'ACTIVE' ? ` · ${u.status}` : ''}
                      </span>
                    </div>
                  </div>
                  <span className="text-xs font-semibold uppercase text-gray-400">{u.role}</span>
                </label>
              </li>
            );
          })
        )}
      </ul>

      <Input
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Reason for assignment (optional)"
      />

      {assign.isError ? (
        <p className="text-sm text-red-600">{apiErrorMessage(assign.error)}</p>
      ) : null}

      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button
          disabled={chosenIds.length === 0 || assign.isPending}
          onClick={() =>
            chosenIds.length > 0 &&
            assign.mutate(
              { id: txId, associateUserIds: chosenIds, reason: reason.trim() || undefined },
              { onSuccess: onDone },
            )
          }
        >
          {assign.isPending ? 'Assigning…' : `Assign (${chosenIds.length})`}
        </Button>
      </div>
    </div>
  );
}

function ReasonPanel({
  title,
  description,
  kind,
  txId,
  onDone,
}: {
  title: string;
  description: string;
  kind: 'revoke' | 'cancel';
  txId: Uuid;
  onDone: () => void;
  onSubmit?: (reason: string) => unknown;
}) {
  const [reason, setReason] = useState('');
  const revoke = useRevokeAssignment();
  const cancel = useForceCancel();
  const minLen = kind === 'revoke' ? 3 : 5;
  const m = kind === 'revoke' ? revoke : cancel;

  function submit() {
    if (reason.trim().length < minLen) return;
    if (kind === 'revoke') revoke.mutate({ id: txId, reason: reason.trim() }, { onSuccess: onDone });
    else cancel.mutate({ id: txId, reason: reason.trim() }, { onSuccess: onDone });
  }

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        <p className="mt-1 text-sm text-gray-600">{description}</p>
      </div>
      <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={`Reason (min ${minLen} chars)`} />
      {m.isError ? <p className="text-sm text-red-600">{apiErrorMessage(m.error)}</p> : null}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onDone}>Back</Button>
        <Button disabled={reason.trim().length < minLen || m.isPending} onClick={submit}>
          {m.isPending ? 'Working…' : 'Confirm'}
        </Button>
      </div>
    </div>
  );
}
